import { Form, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AuthLayout from "@/layouts/auth_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/forms/password-input";
import { FormErrors } from "@/components/forms/form-errors";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

type Mode = "verification" | "confirmation" | "password" | "session";
export default function CustomerAuth({
  mode,
  token,
  sent,
  intake_submitted,
  continue_path,
}: {
  mode: Mode;
  token?: string;
  sent?: boolean;
  intake_submitted?: boolean;
  continue_path?: string;
}) {
  const { t } = useTranslation();
  const { errors } = usePage().props;
  const title = t(`customer_portal.${mode}_title`);
  const fields =
    mode === "password"
      ? ["password", "password_confirmation"]
      : mode === "session"
        ? ["email_address", "password"]
        : mode === "verification"
          ? ["email_address"]
          : [];
  const action =
    mode === "confirmation"
      ? "/customer_portal/email_verifications/confirmation"
      : mode === "verification"
        ? "/customer_portal/email_verifications"
        : `/customer_portal/${mode}`;
  return (
    <AuthLayout title={title} description={t("customer_portal.help")}>
      <Card>
        <CardContent className="space-y-4 pt-6">
          {intake_submitted && (
            <div role="status" className="space-y-2 rounded-lg border p-4">
              <p>{t("website_requests.submitted")}</p>
              <p className="text-muted-foreground text-sm">
                {t("access.optional_password")}
              </p>
            </div>
          )}
          {sent ? (
            <p role="status">{t("customer_portal.sent")}</p>
          ) : (
            <Form
              key={mode}
              action={action}
              method="post"
              disableWhileProcessing
            >
              {({ processing }) => (
                <div className="space-y-4">
                  <FormErrors
                    errors={errors || {}}
                    targets={Object.fromEntries(
                      fields.map((field) => [field, `customer-${field}`]),
                    )}
                  />
                  {token && <input type="hidden" name="token" value={token} />}
                  {mode === "password" && (
                    <p className="text-muted-foreground text-sm">
                      {t("customer_portal.password_help")}
                    </p>
                  )}
                  {fields.map((field, index) => {
                    const FieldInput =
                      field === "email_address" ? Input : PasswordInput;
                    return (
                      <div className="space-y-2" key={field}>
                        <Label htmlFor={`customer-${field}`}>
                          {t(`customer_portal.${field}`)}
                        </Label>
                        <FieldInput
                          id={`customer-${field}`}
                          name={field}
                          type={field === "email_address" ? "email" : undefined}
                          required
                          autoFocus={index === 0}
                          minLength={
                            field !== "email_address" && mode === "password"
                              ? 12
                              : undefined
                          }
                          autoComplete={
                            field === "email_address"
                              ? "email"
                              : mode === "session"
                                ? "current-password"
                                : "new-password"
                          }
                          aria-invalid={!!errors?.[field]}
                        />
                      </div>
                    );
                  })}
                  <Button
                    type="submit"
                    disabled={processing}
                    className="w-full"
                  >
                    {t(`customer_portal.${mode}_submit`)}
                  </Button>
                </div>
              )}
            </Form>
          )}
          {intake_submitted && continue_path && (
            <Link className="inline-block underline" href={continue_path}>
              {t("access.set_password_later")}
            </Link>
          )}
          <div className="flex flex-wrap gap-4 text-sm underline">
            {mode !== "session" && (
              <Link href="/customer_portal/session/new">
                {t("customer_portal.session_title")}
              </Link>
            )}
            {mode === "session" && (
              <Link href="/customer_portal/email_verifications/new">
                {t("customer_portal.verification_title")}
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
